/// Renders GeoJSON water features. Two passes:
///   - Polygon / MultiPolygon → filled water bodies (lakes, harbors,
///     reservoirs, the Great Lakes). Each polygon's outer ring is
///     filled in its own sub-shape so the even-odd fill rule doesn't
///     glitch on dense datasets like nj_rivers / boston_rivers.
///   - LineString / MultiLineString → thin water-blue polylines for
///     rivers, streams, coastlines.
///
/// Both passes use the same `radarTheme.river` color so lakes and
/// rivers read as one continuous water layer.

import { Container, Graphics } from 'pixi.js'
import type { CoordinateProjector } from '../geo/CoordinateProjector'
import type {
  FeatureCollection,
  LineString,
  MultiLineString,
  MultiPolygon,
  Polygon,
  Position,
} from '../types/GeoJSON'
import { radarTheme } from './theme'

export class RiversLayer extends Container {
  constructor(features: FeatureCollection, projector: CoordinateProjector) {
    super()
    // Fills go on the bottom so river lines drawn on top of a lake
    // (e.g., a stream emptying into Lake Michigan) remain visible.
    const fills = new Graphics()
    const lines = new Graphics()
    this.addChild(fills)
    this.addChild(lines)

    for (const feat of features.features) {
      if (!feat.geometry) continue
      const g = feat.geometry
      switch (g.type) {
        case 'Polygon':
          drawPolygon(fills, (g as Polygon).coordinates, projector)
          break
        case 'MultiPolygon':
          for (const poly of (g as MultiPolygon).coordinates) {
            drawPolygon(fills, poly, projector)
          }
          break
        case 'LineString':
          drawLine(lines, (g as LineString).coordinates, projector)
          break
        case 'MultiLineString':
          for (const line of (g as MultiLineString).coordinates) {
            drawLine(lines, line, projector)
          }
          break
        default:
          break
      }
    }

    lines.stroke({ width: 1.2, color: radarTheme.river, alpha: 0.9 })
  }
}

/// Project + fill the outer ring of one Polygon. Inner rings (lake
/// islands) are skipped — at radar zoom they'd be sub-pixel and the
/// extra geometry isn't worth the per-frame cost.
function drawPolygon(
  g: Graphics,
  rings: Position[][],
  projector: CoordinateProjector,
): void {
  const outer = rings[0]
  if (!outer || outer.length < 3) return
  const points: number[] = []
  for (const [lon, lat] of outer) {
    const p = projector.project({ latitude: lat, longitude: lon }, true)
    points.push(p.x, p.y)
  }
  g.poly(points)
  g.fill({ color: radarTheme.river, alpha: 0.55 })
}

function drawLine(
  g: Graphics,
  line: Position[],
  projector: CoordinateProjector,
): void {
  if (line.length < 2) return
  let started = false
  for (const pos of line) {
    const [lon, lat] = pos
    const p = projector.project({ latitude: lat, longitude: lon }, true)
    if (!started) {
      g.moveTo(p.x, p.y)
      started = true
    } else {
      g.lineTo(p.x, p.y)
    }
  }
}
